import { useState, useCallback, useEffect } from 'react';
import { useEditableTranslation } from '#common/i18n/edit-mode.tsx';
import { type ProgressState, completeStep } from '../../../common/config/progress-stages';

export function useProgressTracking() {
    const { rawT } = useEditableTranslation('audio-generation');
    const [ progressState, setProgressState ] = useState<ProgressState | null>(null);
    const [ progressInterval, setProgressInterval ] = useState<NodeJS.Timeout | null>(null);
    const [ stepTimeoutWarning, setStepTimeoutWarning ] = useState<string | null>(null);
    const [ progressError, setProgressError ] = useState<string | null>(null);

    const startProgress = useCallback((steps: Array<{ name: string; estimatedMs: number }>) => {
        const initialTotal = steps.reduce((sum, step) => sum + step.estimatedMs, 0);
        const now = Date.now();

        const initialState: ProgressState = {
            steps,
            currentStepIndex: 0,
            stepStartTime: now,
            processStartTime: now,
            initialTotalMs: initialTotal,
            fixedOverrunMs: 0
        };

        setProgressState(initialState);
        const interval = setInterval(() => {
            setProgressState(prev => {
                if (!prev) {
                    return null;
                }
                return { ...prev };
            });
        }, 100);

        setProgressInterval(interval);
    }, []);

    const updateStage = useCallback((stageName: string) => {
        setProgressState(prev => {
            if (!prev) {
                return null;
            }

            const stageMap: Record<string, number> = {
                IN_QUEUE: 0,
                MODERATION: 1,
                GENERATION: 2,
                DONE: 3
            };

            const newStageIndex = stageMap[stageName];

            if (newStageIndex !== undefined && newStageIndex > prev.currentStepIndex) {
                let currentState = prev;

                for (let i = prev.currentStepIndex; i < newStageIndex; i++) {
                    const stepActualDuration = Date.now() - currentState.stepStartTime;
                    currentState = completeStep(currentState, stepActualDuration);
                }

                return currentState;
            }

            return prev;
        });
    }, []);

    const clearProgress = useCallback(() => {
        if (progressInterval) {
            clearInterval(progressInterval);
            setProgressInterval(null);
        }
        setProgressState(null);
        setStepTimeoutWarning(null);
    }, [ progressInterval, setProgressInterval, setProgressState, setStepTimeoutWarning ]);

    // Step timeout warning effect
    useEffect(() => {
        if (!progressState) {
            setStepTimeoutWarning(null);
            return;
        }

        const currentStep = progressState.steps[progressState.currentStepIndex];
        const stepElapsed = Date.now() - progressState.stepStartTime;
        const stepExpected = currentStep?.estimatedMs || 0;
        const hasValidEstimate = stepExpected > 0;
        const isStepOverdue = hasValidEstimate && stepElapsed > stepExpected * 1.5;

        if (isStepOverdue && currentStep) {
            const warningMessage = rawT('step-timeout-warning', { stepName: currentStep.name });
            setStepTimeoutWarning(warningMessage);
        } else {
            setStepTimeoutWarning(null);
        }
    }, [ progressState, setStepTimeoutWarning, rawT ]);

    return {
        progressState,
        stepTimeoutWarning,
        startProgress,
        updateStage,
        clearProgress,
        progressError,
        setProgressError
    };
}
